(ns app.front.ui.section
  (:require [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.expandable :as expandable]
            [app.front.ui.typography :refer [h3]]
            [app.front.util :as futil]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-db ::toggle-collapsed
  (fn [db [_ id requested-state]]
    (let [prev-state (get-in db [::state id :collapsed?])]
      (if (and (some? requested-state) (not= prev-state requested-state))
        (assoc-in db [::state id :collapsed?] requested-state)
        db))))


(rf/reg-sub ::state
  (fn [db [_ id]]
    (get-in db [::state id])))


(rf/reg-sub ::initially-collapsed?
  :<- [:session/user]
  (fn [user _]
    (-> user :user/authority? not)))


(defn- expandable-section [{:keys [section-id initially-collapsed?] :as props} component-fn]
  (let [state           (or (<sub [::state section-id]) {:collapsed? initially-collapsed?})
        toggle-state-fn (fn [requested-state] (rf/dispatch [::toggle-collapsed section-id requested-state]))]
    [expandable/expandable
     (merge (select-keys props [:on-expand :transition-duration])
            (util/mmap state toggle-state-fn))
     component-fn]))


(def transition-duration 240)

(defn get-section-id [id]
  (str "section--" (futil/sanitize-id id)))

(defn- section-content
  "Section common component with optional heading and expandable content"
  [id
   {:keys [tr class dnd-config heading-button heading-extra heading-class heading-tag heading-tag-color loading?
           initially-collapsed? error? heading-component always-expanded?
           ;; optional callback to execute when expanding
           on-expand]}
   heading
   children]
  {:pre [(some? tr)]}

  (let [section-id (get-section-id id)]
    [expandable-section (util/mmap section-id on-expand initially-collapsed? transition-duration)
     (fn [{:keys [toggle-fn collapsed? expandable-container-props]}]
       [:section.expandable
        {:id      section-id
         :data-cy section-id
         :class   class}
        [:div.section-heading
         dnd-config
         [:div.flex.flex-1.flex-wrap.items-center.justify-between.gap-2
          [:div
           (if heading-component
             [heading-component toggle-fn collapsed?]
             [(if always-expanded?
                :h2.flex.items-center
                :h2.flex.items-center.cursor-pointer)
              {:on-click (when-not always-expanded? toggle-fn)
               :class    heading-class}
              (when heading-tag
                [ui/pill {:text  heading-tag
                          :color heading-tag-color}])
              (when error?
                [cc/icon {:icon        i/circle-attention
                          :description (tr [:error/error])
                          :fill        "red"
                          :class-name  "mr-2"}])
              (when (some? heading)
                (tr [heading]))])]
          heading-extra
          (when (some? heading-button)
            (if loading?
              (cc/button-skeleton)
              [:div {:on-click (fn [_]
                                 (when collapsed? (toggle-fn)))}
               heading-button]))]
         (when (not always-expanded?)
           [:div.flex.flex-0.items-start
            [ui/expand-button
             {:button-props    {:data-cy    (str "expand-section-" id)
                                :class-name "normal-case font-normal"}
              :expand-handler  toggle-fn
              :title-expanded  (tr [:close])
              :title-collapsed (tr [:open])
              :expanded?       (not collapsed?)
              :tr              tr}]])
         [:div.flex.flex-0.items-center
          (when dnd-config
            (cc/icon {:icon        i/draggable
                      :description (tr [:form.schema/reorder-form-row])
                      :fill        "currentcolor"}))]]
        [:div.section-content.w-full.pt-1
         (expandable-container-props
           {:id (str section-id "-sub")})
         (into [:div.mt-3]
               (if loading?
                 [(cc/skeleton-text {:paragraph true :line-count 3})]
                 children))]])]))


(defn section [id opts heading & children]
  [eb/error-boundary [section-content id opts heading children]])


(defn initially-collapsed-section
  "Section component that is initially collapsed for regular users, expanded for authorities."
  [id state heading & children]
  (let [initially-collapsed? (<sub [::initially-collapsed?])
        state                (merge state {:initially-collapsed? initially-collapsed?})]
    (apply vector section id state heading children)))


(def subsection-base :div.bg-ui-light-gray.mb-2)

(defn subsection-content [{:keys [title subtitle]} & content]
  [:<>
   [:div.p-6.pb-2.border-b.border-solid.border-ui-dark-gray.flex.items-baseline.flex-wrap
    [h3
     (futil/cs "font-bold m-0" (when subtitle "w-2-3"))
     title]
    (when subtitle [:span subtitle])]
   (into [:div.bg-ui-light-gray.p-6]
         content)])

(defn subsection [title-opts & content]
  [subsection-base
   (into [subsection-content title-opts] content)])

(defn- make-button [{:keys [icon description on-click kind]}]
  (cc/button (util/mmap {:kind (or kind :tertiary)
                         :size :small}
                        icon on-click)
             description))

(defn- make-buttons [button-definitions]
  (->> button-definitions
       (remove nil?)
       (map make-button)
       (into [:<>])))

(defn subsection-2
  "An expandable subsection for use with a non-exdpandable section.

  `:header-buttons` have keys `:icon`, `:description`, `:on-click`, `:kind`"
  [{:keys [initially-collapsed? class title title-class tr header-buttons] :or {initially-collapsed? false}} & children]
  (r/with-let [*collapsed (r/atom initially-collapsed?)]
    [expandable/expandable
     (util/mmap {:toggle-state-fn (fn [new-state]
                                    (reset! *collapsed new-state))
                 :state           {:collapsed? @*collapsed}})
     (fn [{:keys [toggle-fn collapsed? expandable-container-props]}]
       [:div {:class (or class "border rounded-md border-solid p-4 my-2")}
        [:div.flex.justify-between.items-center.flex-wrap.justify-end.gap-y-2
         [:div {:class (or title-class "text-lg font-bold")} title]
         [:div.flex.align-baseline.gap-2.flex-wrap.justify-end.flex-1
          [make-buttons (->> header-buttons
                             (map (fn [button-def]
                                    (some-> button-def
                                            (update :on-click (fn [old-handler]
                                                                (fn [& args]
                                                                  (when @*collapsed
                                                                    (toggle-fn))
                                                                  (apply old-handler args))))))))]
          [ui/expand-button (util/mmap {:expand-handler toggle-fn
                                        :expanded?      (not collapsed?)}
                                       tr)]]]
        [:div (expandable-container-props)
         (into [:div.mt-4] children)]])]))

(defn subsection-3
  "A very subtle title + button arrangement

   `:header-buttons` have keys `:icon`, `:description`, `:on-click`, `:kind`"
  [{:keys [title header-buttons header-extra class]} & children]
  (into [:div (when class (util/mmap class))
         [:div.flex.w-full.justify-between.items-baseline.mb-6
          [:h3.m-0.text-sm.uppercase.font-bold title]
          [:div.flex.flex-wrap.justify-end.gap-2
           header-extra
           [make-buttons header-buttons]]]]
        children))

(defn tabbed-section
  "A section with content-switcher on top for picking which 'tab' of the section is displayed

  Props:
  :selected-tab-index  required, the index of the currently selected tab
  :on-change           required, is called with the tab index when the user selects a tab
  :tabs                required, a vector containing maps
  :header-extra        optional, element to be rendered on the right side of the content switcher, e.g. a button

  Tab props:
  :label-text     required, the string that is used in the content switcher for the tab
  :content        required, the reagent element for the tab contents"
  [{:keys [selected-tab-index on-change tabs header-extra]}]
  [:<>
   [:div.bg-transparent.m-0.p-0.pb-2.border-solid.border-b.border-ui-light-gray
    [:div.flex.flex-col.items-start.overflow-auto.lg:flex-row.lg:justify-between.lg:gap-2
     [ui-cs/content-switcher
      {:current   selected-tab-index
       :on-change on-change
       :items     (-> tabs count range)
       :val->str  #(-> tabs (get %) :label-text)
       :class     "mb-4"}]
     header-extra]]
   [:section {:style {:margin-top 0}}
    (-> tabs
        (nth selected-tab-index)
        :content)]])

(defn section-skeleton []
  [:section.p-8
   [:div.mb-8.mr-8 (cc/skeleton-text {:heading true})]
   (cc/skeleton-text {:paragraph true})])
